/**
 * Base API client with common utilities for making HTTP requests.
 * All domain-specific API modules import from this file.
 */

import { APIResponse } from '../../types';
import { isInterceptablePath, redirectToReauth, refreshSession } from './sessionRefresh';

// API Configuration - empty string means same origin (all services unified in backend)
export const SQUARE_API_URL = '';  // Use same origin
export const AI_API_URL = '';      // Use same origin

// Build API base URL for Square operations
export const SQUARE_API_V2_BASE = '/api/square';

function buildAuthHeaders(options: RequestInit): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Include Bearer token from localStorage for cross-subdomain auth,
  // falling back to the httpOnly cookie sent via credentials: 'include'.
  const storedToken = localStorage.getItem('auth_token');
  if (storedToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${storedToken}`;
  }

  return headers;
}

/**
 * Core API request function with error handling and logging.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  apiType: 'square' | 'ai' = 'square'
): Promise<APIResponse<T>> {
  try {
    // All APIs now use the same backend through /api/* routes
    let url: string;
    if (apiType === 'ai') {
      // AI endpoints go through /api/ai/*
      url = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
    } else {
      // Square endpoints already have /api/v2 prefix
      url = SQUARE_API_V2_BASE + endpoint;
    }

    console.log(`Making API request to: ${url}`);
    console.log('Request options:', {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body ? 'Body present (length: ' + (options.body as string).length + ')' : 'No body'
    });

    const headers = buildAuthHeaders(options);

    // Special handling for ngrok URLs
    if (url.includes('ngrok')) {
      console.warn('Using ngrok URL - make sure ngrok tunnel is active and accessible');
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const requestInit: RequestInit = {
      headers,
      credentials: 'include', // Include cookies for authentication
      ...options,
    };
    let response = await fetch(url, requestInit);

    if (response.status === 401 && isInterceptablePath(url)) {
      response = await handleUnauthorized(url, requestInit, response);
    }

    console.log(`Response status: ${response.status} ${response.statusText}`);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    // Handle 404 for collection endpoints as empty data (common pattern for empty collections)
    if (response.status === 404 && options.method !== 'POST' && options.method !== 'PUT' && options.method !== 'DELETE') {
      const isCollectionEndpoint = endpoint.includes('/products') ||
        endpoint.includes('/attributes') ||
        endpoint.includes('/categories');

      if (isCollectionEndpoint) {
        console.log('Treating 404 as empty collection for endpoint:', endpoint);

        // Return appropriate empty structure based on endpoint
        if (endpoint.includes('/products')) {
          // Return empty paginated response for products
          const emptyPaginatedResponse = {
            data: [],
            total: 0,
            page: 1,
            page_size: 12,
            total_pages: 0
          };
          return { data: emptyPaginatedResponse as T, success: true };
        } else {
          // Return empty array for attributes/categories
          return { data: [] as T, success: true };
        }
      }
    }

    // Check if we received HTML instead of JSON (common when API URL is wrong)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON - API URL may be incorrect');
      return {
        success: false,
        message: `Received HTML response instead of JSON. Check if ${apiType.toUpperCase()} API URL is correct and the server is running.`,
      };
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        // If we can't parse JSON, it might be HTML or plain text
        const textResponse = await response.text().catch(() => 'Unknown error');
        console.error('Failed to parse error response as JSON:', textResponse);
        return {
          success: false,
          message: `HTTP ${response.status}: ${response.statusText}. Server returned non-JSON response.`,
          status: response.status,
        };
      }
      console.error('API request failed:', errorData);
      return {
        success: false,
        message: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      };
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return { success: true };
    }

    const data = await response.json();
    console.log('API response data:', data);
    return { data, success: true };
  } catch (error) {
    console.error('API request error:', error);

    // Provide more specific error messages
    let errorMessage = 'Network error';
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      errorMessage = 'Failed to connect to API server. Check if the server is running and accessible.';
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
}

/**
 * Handles a 401 on an interceptable request: attempts a single silent
 * refresh (shared across concurrent 401s via single-flight), then retries
 * the original request exactly once on success.
 *
 * On refresh failure, kicks off the re-login redirect and returns the
 * original 401 `Response` unchanged: no retry, since a second request
 * against a session that failed to refresh would just 401 again. The
 * caller's existing 401 error path runs while the redirect navigates away.
 *
 * If the retried request ALSO comes back 401 (refresh cookie was valid but
 * the session is genuinely dead, or a race with another tab's logout), the
 * retry is not retried again: the redirect fires here too, so the user is
 * never left staring at a raw "HTTP 401" error with no path back to login.
 *
 * INVARIANT: retrying a mutating request (POST/PUT/DELETE) here is safe from
 * double-submission. Auth on this backend runs as a FastAPI dependency
 * (`require_auth_cookie`) that executes BEFORE the route handler body, and
 * production logs show 401s rejected in 33-96ms: well before any handler
 * effect could run. A 401 therefore means the handler never executed, so the
 * retry is the request's first real attempt, not a second one. Do not
 * method-gate this retry (skipping POST/PUT/DELETE would break the AI
 * execute POST, the card's primary use case). If a future backend change
 * moves auth checks after the handler runs (or into the handler itself),
 * this invariant breaks and the retry must be revisited.
 */
async function handleUnauthorized(url: string, requestInit: RequestInit, original401: Response): Promise<Response> {
  const refreshed = await refreshSession();
  if (!refreshed) {
    redirectToReauth();
    return original401;
  }
  const retried = await fetch(url, requestInit);
  if (retried.status === 401) {
    redirectToReauth();
  }
  return retried;
}

/**
 * Retry requests with exponential backoff for rate limiting.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<APIResponse<T>>,
  maxRetries: number = 2,
  baseDelay: number = 1000,
  onRetry?: (attempt: number, delay: number) => void
): Promise<APIResponse<T>> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();

      // If success, return immediately
      if (result.success) {
        return result;
      }

      // Check if it's a rate limit error that we should retry
      if (result.message && (result.message.includes('busy') || result.message.includes('429') || result.message.includes('Too Many Requests'))) {
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
          console.log(`Rate limited (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
          if (onRetry) {
            onRetry(attempt + 1, delay);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }

      // For non-rate-limit errors or final attempt, return the result
      return result;
    } catch (error) {
      // For network errors, retry if not final attempt
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`, error);
        if (onRetry) {
          onRetry(attempt + 1, delay);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Final attempt failed
      return {
        success: false,
        message: `Request failed after ${maxRetries + 1} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // This shouldn't be reached, but TypeScript needs it
  return {
    success: false,
    message: 'Unexpected error in retry logic'
  };
}

/**
 * File upload request handler for multipart/form-data.
 */
export async function apiFileUploadRequest<T>(
  endpoint: string,
  formData: FormData,
  apiType: 'square' | 'ai' = 'ai'
): Promise<APIResponse<T>> {
  try {
    // Ensure the URL is properly formed
    let url: string;
    if (apiType === 'ai') {
      // AI endpoints should use the same origin with the endpoint
      url = endpoint.startsWith('/') ? endpoint : '/' + endpoint;
    } else {
      // Square endpoints use SQUARE_API_V2_BASE
      url = SQUARE_API_V2_BASE + endpoint;
    }

    console.log(`Making file upload request to: ${url}`);
    console.log('FormData entries:', Array.from(formData.entries()).map(([key, value]) => [key, value instanceof File ? `File: ${value.name}` : value]));

    const headers: Record<string, string> = {};

    // Include Bearer token from localStorage for cross-subdomain auth,
    // falling back to the httpOnly cookie sent via credentials: 'include'.
    const uploadToken = localStorage.getItem('auth_token');
    if (uploadToken) {
      headers['Authorization'] = `Bearer ${uploadToken}`;
    }

    // Add ngrok-skip-browser-warning header for ngrok URLs
    if (url.includes('ngrok')) {
      console.warn('Using ngrok URL for file upload - make sure ngrok tunnel is active and accessible');
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const uploadInit: RequestInit = {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include', // Include cookies for authentication
      // Don't set Content-Type header - let the browser set it with boundary for multipart/form-data
    };
    let response = await fetch(url, uploadInit);

    if (response.status === 401 && isInterceptablePath(url)) {
      response = await handleUnauthorized(url, uploadInit, response);
    }

    console.log(`Response status: ${response.status} ${response.statusText}`);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        // If we can't parse JSON, try to get the response as text for better debugging
        try {
          const textResponse = await response.text();
          console.error('File upload failed with non-JSON response:', textResponse);
          return {
            success: false,
            message: `HTTP ${response.status}: ${response.statusText}. Server response: ${textResponse || 'No response body'}`,
          };
        } catch (textError) {
          console.error('Failed to read response as text:', textError);
          return {
            success: false,
            message: `HTTP ${response.status}: ${response.statusText}. Unable to read server response.`,
          };
        }
      }
      console.error('File upload request failed:', errorData);

      // Handle rate limit specifically
      if (response.status === 429) {
        return {
          success: false,
          message: 'AI service is currently busy. Please wait a moment and try again.',
        };
      }

      return {
        success: false,
        message: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const data = await response.json();
    console.log('File upload response data:', data);
    return { data, success: true };
  } catch (error) {
    console.error('File upload request error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error',
    };
  }
}
